import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { MultiSelect } from "@/components/design/MultiSelect";
import { TextInput } from "@/components/design/TextField";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { useDebounced } from "@/lib/use-debounced";
import { useGlobalPersonSearch } from "@/lib/use-global-person-search";
import { MyPersonGroupsDocument, MyPinGroupsDocument, PinType } from "@/graphql/generated/operations";
import type { ResultPerson } from "./match-result-derive";

const SEARCH_PAGE_SIZE = 12;
const ALL_PINNED_VALUE = "__all_pinned__";
interface SavedSet { id: string; kind: "personGroup" | "pinGroup" | "allPinned"; name: string; }

export function TeamMemberPicker({
  open,
  onClose,
  chosenIds,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  chosenIds: readonly string[];
  onPick: (person: ResultPerson) => void;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [savedSetIds, setSavedSetIds] = useState<string[]>([]);
  const debouncedQuery = useDebounced(query, 250);

  const personGroupsQuery = useQuery(MyPersonGroupsDocument, { skip: !open });
  const pinGroupsQuery = useQuery(MyPinGroupsDocument, {
    variables: { input: { pinTypes: [PinType.Person] } },
    skip: !open,
  });

  const savedSets = useMemo((): SavedSet[] => {
    const out: SavedSet[] = [
      {
        id: ALL_PINNED_VALUE,
        kind: "allPinned",
        name: intl.formatMessage({ id: "match.people.allPinned", defaultMessage: "All pinned" }),
      },
    ];
    const groupResult = personGroupsQuery.data?.personGroups;
    if (groupResult?.__typename === "PersonGroupResult") {
      for (const g of groupResult.personGroups ?? []) {
        out.push({ id: g.id, kind: "personGroup", name: g.name });
      }
    }
    const pinResult = pinGroupsQuery.data?.pinGroups;
    if (pinResult?.__typename === "PinGroupResult") {
      for (const g of pinResult.pinGroups ?? []) {
        out.push({ id: g.id, kind: "pinGroup", name: g.name });
      }
    }
    return out;
  }, [intl, personGroupsQuery.data, pinGroupsQuery.data]);

  const where = useMemo(() => {
    if (savedSetIds.length === 0) return null;
    const clauses: Record<string, unknown>[] = savedSetIds.map((id) => {
      const set = savedSets.find((s) => s.id === id);
      if (set?.kind === "personGroup") return { personGroupId: { eq: id } };
      if (set?.kind === "pinGroup") return { pinGroupId: { eq: id } };
      return { isPinned: { eq: true } };
    });
    const single = clauses[0];
    return clauses.length === 1 && single ? single : { or: clauses };
  }, [savedSetIds, savedSets]);

  const search = useGlobalPersonSearch({
    query: debouncedQuery,
    personFilter: where,
    pageSize: SEARCH_PAGE_SIZE,
    skip: !open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      id="team-fit-picker"
      maxWidth="36rem"
      title={<FormattedMessage id="match.teamFit.pickerTitle" defaultMessage="Current team" />}
    >
      <div className="space-y-4">
        <TextInput
          id="team-fit-picker-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={intl.formatMessage({
            id: "match.teamFit.searchPlaceholder",
            defaultMessage: "Search by name",
          })}
        />
        <MultiSelect
          id="team-fit-picker-sets"
          selected={savedSetIds}
          onChange={setSavedSetIds}
          options={savedSets.map((s) => ({ value: s.id, label: s.name }))}
          placeholder={intl.formatMessage({
            id: "match.teamFit.savedSets",
            defaultMessage: "Groups and pins",
          })}
        />
        {search.nodes.length === 0 ? (
          <div className="bg-page box-flat py-12 text-center text-smycles-meta text-ink-muted">
            {search.loading
              ? intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })
              : intl.formatMessage({ id: "match.noPeopleFound", defaultMessage: "No people found" })}
          </div>
        ) : null}
        <ul>
          {search.nodes.map((node) => {
            const chosen = chosenIds.includes(node.id);
            return (
              <li key={node.id} className="border-t border-ink/5 first:border-t-0">
                <PersonResultRow
                  embedded
                  to={null}
                  person={{
                    id: node.id,
                    name: personDisplayName(node),
                    avatarContent: node.avatar?.content ?? null,
                    avatarMime: node.avatar?.mimeType ?? null,
                  }}
                  title={personDisplayName(node)}
                  sub={personDisplayRole(node)}
                  actions={
                    <Button
                      variant="chip"
                      size="sm"
                      disabled={chosen}
                      onClick={() =>
                        onPick({
                          id: node.id,
                          name: personDisplayName(node),
                          profession: personDisplayRole(node),
                          avatarContent: node.avatar?.content ?? null,
                          avatarMime: node.avatar?.mimeType ?? null,
                        })
                      }
                    >
                      {chosen ? (
                        <FormattedMessage id="match.teamFit.added" defaultMessage="Added" />
                      ) : (
                        <FormattedMessage id="match.teamFit.add" defaultMessage="Add" />
                      )}
                    </Button>
                  }
                />
              </li>
            );
          })}
        </ul>
        {/* Ohne Nachladen endet die Liste nach SEARCH_PAGE_SIZE Treffern, und wer nicht unter den
            ersten zwoelf steht, ist nur ueber eine engere Suche erreichbar. */}
        {search.hasMore ? (
          <div className="text-center">
            <Button
              id="team-fit-picker-more"
              variant="secondary"
              size="sm"
              disabled={search.loadingMore}
              onClick={() => search.loadMore()}
            >
              {search.loadingMore
                ? intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })
                : intl.formatMessage({ id: "common.loadMore", defaultMessage: "Load more" })}
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
